"use client";

import React from "react";
import { MapPin, Clock, Wrench, ShieldCheck, ArrowRight, CircleCheck, Settings2, Maximize } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const services = [{
  id: 1,
  title: "Efficiency Metrics",
  desc: "Quantitative analysis of operational throughput and resource allocation across all departmental silos.",
  iconName: "settings"
}, {
  id: 2,
  title: "Quality Assurance",
  desc: "Rigorous benchmarking against international standards to ensure consistent output excellence.",
  iconName: "wrench"
}, {
  id: 3,
  title: "Strategic Impact",
  desc: "Measuring the long-term value creation and market positioning resulting from core initiatives.",
  iconName: "maximize"
}, {
  id: 4,
  title: "Risk Mitigation",
  desc: "Comprehensive evaluation of potential vulnerabilities and the robustness of existing safeguards.",
  iconName: "shield"
}];
const neighborhoods = [{
  id: 1,
  name: "Optimal Zone"
}, {
  id: 2,
  name: "Target Range"
}, {
  id: 3,
  name: "Growth Area"
}, {
  id: 4,
  name: "Baseline"
}, {
  id: 5,
  name: "Critical Path"
}, {
  id: 6,
  name: "Sub-optimal"
}];
const renderIcon = (iconName: string, className: string) => {
  switch (iconName) {
    case "settings":
      return <Settings2 className={className} />;
    case "wrench":
      return <Wrench className={className} />;
    case "maximize":
      return <Maximize className={className} />;
    case "shield":
      return <ShieldCheck className={className} />;
    default:
      return <Wrench className={className} />;
  }
};
export default function LocalServicesInLaval() {
  return <section data-section-id="252549" id="scoring-components-scale" className="bg-black text-white py-16 md:py-24 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      <div className="page-container">
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 bg-white/10 text-white hover:bg-white/20 rounded-none border border-white/20">
            <Settings2 className="w-4 h-4 mr-1 text-white" />
            Analytical Framework
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-default mb-6 max-w-3xl">
            Scoring Components & Methodology
          </h2>
          <p className="text-lg text-white/80 max-w-2xl font-sans">
            Our proprietary scoring engine evaluates performance across four critical dimensions, providing a transparent and data-driven assessment of organizational health.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Grid: Service Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {services.map((service, i) => <Card key={service.id} data-index={i} className="border-white border-[1px] rounded-none shadow-none hover:bg-white/5 transition-colors duration-300 bg-transparent">
                <CardHeader>
                  <div className="w-12 h-12 rounded-none bg-transparent flex items-center justify-center mb-4">
                    {renderIcon(service.iconName, "w-6 h-6 text-white stroke-[1px]")}
                  </div>
                  <CardTitle className="text-[20px] font-default text-white">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[16px] text-white/70 font-sans">
                    {service.desc}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>

          {/* Right Sidebar: Map/Scale Card */}
          <div className="lg:col-span-1">
            <Card className="border-white border-[1px] rounded-none shadow-none bg-transparent h-full flex flex-col overflow-hidden">
              <div className="relative h-48 w-full bg-zinc-900">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/scoring_scale_visualization.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/scoring_scale_visualization.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/scoring_scale_visualization.png-wvc-sizes" alt="Scoring scale data visualization" className="w-full h-full object-cover opacity-80" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              </div>
              
              <CardHeader className="relative -mt-8 pb-4">
                <CardTitle className="text-2xl font-default text-white flex items-center gap-2">
                  <Maximize className="w-6 h-6 text-white stroke-[1px]" />
                  The Scoring Scale
                </CardTitle>
                <CardDescription className="text-base font-sans text-white/60 mt-2">
                  A standardized 0-100 index used to benchmark performance against industry leaders and internal targets.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex-grow flex flex-col gap-6">
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-white/10 p-2 rounded-none">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white font-default">Real-time Calibration</h4>
                    <p className="text-sm text-white/60 font-sans">Data points are refreshed every 24 hours for maximum accuracy.</p>
                  </div>
                </div>

                <Separator className="bg-white/20" />

                <div>
                  <h4 className="font-semibold text-white font-default mb-3">Performance Tiers</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {neighborhoods.map((hood, i) => <li key={hood.id} data-index={i} className="flex items-center gap-2 text-sm font-mono text-white/70">
                        <CircleCheck className={`w-4 h-4 shrink-0 ${i < 3 ? 'text-[hsl(150,60%,35%)]' : 'text-[hsl(0,75%,45%)]'}`} />
                        <span>{hood.name}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="mt-auto pt-6">
                  <Button asChild className="w-full font-default text-base h-12 bg-white text-black hover:bg-white/90 rounded-none" size="lg">
                    <Link to="/MethodologyPage">
                      Explore Methodology
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}